import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { createContext, use, useMemo } from 'react'
import { api } from '@/lib/api'
import { useSession } from '@/lib/auth-client'
import { queryKeys } from '@/lib/query-keys'

type AdminUser = {
  id: string
  email: string
  name: string
  admin_type: 'super_admin'
}

type AuthContextValue = {
  admin: AdminUser | null
  isLoading: boolean
  isAuthenticated: boolean
  error: Error | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: session, isPending: sessionLoading } = useSession()
  const isAuthenticated = !!session?.user?.id

  const {
    data: admin,
    isLoading: adminLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.adminMe,
    queryFn: async () => {
      const { data, error: apiError } = await api.admin.me.get()
      if (apiError)
        throw new Error(
          typeof apiError.value === 'string' ? apiError.value : 'Failed to load admin',
        )
      return data
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const value = useMemo<AuthContextValue>(
    () => ({
      admin: admin ?? null,
      isLoading: sessionLoading || (isAuthenticated && adminLoading),
      isAuthenticated,
      error: (error as Error | null) ?? null,
    }),
    [admin, sessionLoading, adminLoading, isAuthenticated, error],
  )

  return <AuthContext value={value}>{children}</AuthContext>
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
